/* ============================================================================
   4th-level product flyout — desktop open/position (portal-to-<body> fix)
   ----------------------------------------------------------------------------
   The mega-nav dropdown panel (`.main-nav__child`) has `overflow: auto` in
   this theme's CSS (needed so long menus can scroll). Any absolutely-
   positioned descendant that visually extends past that panel's own box
   gets silently clipped by it, no matter the z-index — that's what made the
   product flyout render partially cut off / offset from its link instead of
   appearing cleanly next to it.

   Fix: on open, move the flyout's <details> element to be a direct child of
   <body>, switch it to `position: fixed`, and set its inline top/left from
   the link's own getBoundingClientRect() — clamped to stay inside the
   viewport. A `<!--flyout-anchor-->` comment node left behind marks exactly
   where to put it back on close, so the mobile in-place accordion (and the
   no-js fallback) are completely unaffected.

   Also includes a short close-delay (hover-intent buffer): moving the mouse
   from the link down into the panel always crosses a small gap that
   belongs to neither element, and without a grace period that gap alone
   was enough to close the panel before the pointer ever reached it.

   Standalone — no dependency on the `MainMenu` custom element. Safe to add
   without touching `assets/main.js`.
   ============================================================================ */
(function () {
  'use strict';

  if (typeof document === 'undefined' || !document.querySelectorAll) return;

  var wraps = document.querySelectorAll('.grandchild-nav__link-wrap--has-product-flyout');
  if (!wraps.length) return;

  var ACTIVE_BREAKPOINT = 769; // matches @media (min-width: 769px) in the CSS
  var GUTTER = 12; // minimum breathing room from the viewport edge, in px
  var MD_QUERY = typeof window.matchMedia === 'function'
    ? window.matchMedia('(min-width: ' + ACTIVE_BREAKPOINT + 'px)')
    : null;

  function isDesktop() {
    if (MD_QUERY) return MD_QUERY.matches;
    return window.innerWidth >= ACTIVE_BREAKPOINT;
  }

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () { fn.apply(null, args); }, wait);
    };
  }

  // One entry per grandchild link that has a flyout. Recorded up front,
  // before anything is ever moved, so we always know where each panel
  // started and which wrap it belongs to.
  var entries = [];

  Array.prototype.forEach.call(wraps, function (wrap) {
    var details = wrap.querySelector('.grandchild-nav__product-flyout');
    if (!details) return;

    var anchor = document.createComment('flyout-anchor');
    details.parentNode.insertBefore(anchor, details);

    var entry = { wrap: wrap, details: details, anchor: anchor, portaled: false, closeTimer: null };
    entries.push(entry);

    wrap.addEventListener('mouseenter', function () { open(entry); });
    wrap.addEventListener('focusin', function () { open(entry); });
    wrap.addEventListener('mouseleave', function (evt) { scheduleClose(entry, evt); });
    wrap.addEventListener('focusout', function (evt) { scheduleClose(entry, evt); });
    // The panel itself lives outside `wrap` once portaled, so it needs its
    // own enter/leave/blur listeners: entering it must cancel a pending
    // close from leaving the link, and leaving it (without landing back on
    // the link) must close it.
    details.addEventListener('mouseenter', function () { cancelScheduledClose(entry); });
    details.addEventListener('mouseleave', function (evt) { scheduleClose(entry, evt); });
    details.addEventListener('focusout', function (evt) { scheduleClose(entry, evt); });
  });

  if (!entries.length) return;

  // How long to wait before actually closing after a mouseleave/focusout.
  var CLOSE_DELAY = 300;

  function cancelScheduledClose(entry) {
    if (entry.closeTimer) {
      clearTimeout(entry.closeTimer);
      entry.closeTimer = null;
    }
  }

  function isInsideEntry(entry, node) {
    return !!node && (entry.wrap.contains(node) || entry.details.contains(node));
  }

  function scheduleClose(entry, evt) {
    var related = evt && evt.relatedTarget;
    if (isInsideEntry(entry, related)) return;
    cancelScheduledClose(entry);
    entry.closeTimer = setTimeout(function () {
      entry.closeTimer = null;
      close(entry);
    }, CLOSE_DELAY);
  }

  function portalIn(entry) {
    if (entry.portaled) return;
    document.body.appendChild(entry.details);
    entry.details.classList.add('grandchild-nav__product-flyout--portaled');
    entry.portaled = true;
  }

  function portalOut(entry) {
    if (!entry.portaled) return;
    entry.anchor.parentNode.insertBefore(entry.details, entry.anchor.nextSibling);
    entry.details.classList.remove('grandchild-nav__product-flyout--portaled');
    entry.details.style.left = '';
    entry.details.style.top = '';
    entry.details.style.maxWidth = '';
    entry.portaled = false;
  }

  function position(entry) {
    var wrapRect = entry.wrap.getBoundingClientRect();
    var panelRect = entry.details.getBoundingClientRect();

    // Never let the panel claim more width than the viewport can offer.
    var maxWidth = Math.max(160, Math.min(panelRect.width || 300, window.innerWidth - GUTTER * 2));
    entry.details.style.maxWidth = maxWidth + 'px';
    panelRect = entry.details.getBoundingClientRect();

    var left = wrapRect.left;
    if (left + panelRect.width > window.innerWidth - GUTTER) {
      left = window.innerWidth - GUTTER - panelRect.width;
    }
    if (left < GUTTER) left = GUTTER;

    // No gap between the link and the panel — the panel starts exactly
    // where the link ends, so there's no dead zone for the mouse to cross
    // (the close-delay above is the real safety net, this just removes the
    // easiest way to trip it).
    var top = wrapRect.bottom;
    if (top + panelRect.height > window.innerHeight - GUTTER) {
      var aboveTop = wrapRect.top - panelRect.height;
      if (aboveTop > GUTTER) top = aboveTop; // flip above the link if there's more room there
    }

    entry.details.style.left = Math.round(left) + 'px';
    entry.details.style.top = Math.round(top) + 'px';
  }

  function open(entry) {
    if (!isDesktop()) return;
    cancelScheduledClose(entry);
    portalIn(entry);
    entry.details.open = true;
    position(entry);
  }

  function close(entry) {
    cancelScheduledClose(entry);
    entry.details.open = false;
    portalOut(entry);
  }

  var onResize = debounce(function () {
    if (!isDesktop()) {
      entries.forEach(close);
      return;
    }
    entries.forEach(function (entry) {
      if (entry.details.open) position(entry);
    });
  }, 100);

  var onScroll = debounce(function () {
    entries.forEach(function (entry) {
      if (entry.details.open) position(entry);
    });
  }, 50);

  window.addEventListener('resize', onResize);
  window.addEventListener('on:breakpoint-change', onResize);
  // Capture phase so this fires for scroll on any scrollable ancestor
  // (e.g. the mega-nav panel itself), not just window-level scroll.
  window.addEventListener('scroll', onScroll, true);
})();
